import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import '../styles/HptScreens.css';
import SignaturePad from './SignaturePad';
import imagenFondo from '../assets/logo_elecnor.jpg';
import html2pdf from 'html2pdf.js';

const API_URL = process.env.REACT_APP_API_URL;

const PermisoCarretilla = ({ datosPrevios, onSave, onCancel, readOnly = false, usuarioActual, isPdfMode = false }) => {

    const { id } = useParams();
    const navigate = useNavigate();
    const isStandalone = typeof onSave !== 'function' && !isPdfMode;
    const pdfRef = useRef();
    const [isPrintingLocal, setIsPrintingLocal] = useState(false);
    
    const itemsChecklist = [
        { key: "c1", label: "El pórtico de seguridad está en perfectas condiciones" },
        { key: "c2", label: "El estribo de acceso es correcto" },
        { key: "c3", label: "Los espejos retrovisores están correctos" },
        { key: "c4", label: "Se ha revisado la placa identificativa para comprobar el peso máximo que se puede manipular" },
        { key: "c5", label: "El mástil y las horquillas están en perfectas condiciones" },
        { key: "c6", label: "El estado de la superficie de rodadura e hinchado de los neumáticos es correcto" },
        { key: "c7", label: "Funcionan correctamente los frenos (de pie y de mano), la dirección, los mandos, el alumbrado, intermitentes y la señalización acústica (marcha atrás, bocina) y lumínica" },
        { key: "c8", label: "La posición es correcta y está debidamente fijada, de todos los protectores y elementos de seguridad así como de los brazos de horquilla o de los accesorios" },
        { key: "c9", label: "Son adecuados los niveles de combustible, fluidos, refrigerantes, etc." },
        { key: "c10", label: "Inexistencia de fugas de fluidos (gasoil, aceites, etc)" },
        { key: "c11", label: "Es adecuado el estado del puesto de conducción, estando libre de objetos y/o herramientas que puedan desplazarse libremente y llegar a bloquear un mando o impedir una maniobra cuando sea necesario" },
        { key: "c12", label: "El cinturón de seguridad funciona correctamente" }
    ];

    const rastroEstadoInicial = {
        fechaInspeccion: new Date().toISOString().split('T')[0],
        horaInspeccion: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
        numeroSerie: '', 
        observaciones: '',
        firmaJefeTurno: null, 
        firmaJefeTrabajo: null, 
        firmaRecursoPreventivo: null
    };

    itemsChecklist.forEach(item => {
        rastroEstadoInicial[item.key] = ''; 
    });

    const [formData, setFormData] = useState(datosPrevios || rastroEstadoInicial);
    const [firmaActiva, setFirmaActiva] = useState(null);

    useEffect(() => {
        if (isStandalone && id) {
            const cargarDatos = async () => {
                try {
                    const token = localStorage.getItem('userToken');
                    const res = await axios.get(`${API_URL}/api/hpt/carretilla/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    const dbData = res.data;
                    setFormData({
                        fechaInspeccion: dbData.fecha_inspeccion || "",
                        horaInspeccion: dbData.hora_inspeccion || "",
                        numeroSerie: dbData.numero_serie || "",
                        observaciones: dbData.observaciones || "",
                        c1: dbData.check_c1 || "", c2: dbData.check_c2 || "", c3: dbData.check_c3 || "",
                        c4: dbData.check_c4 || "", c5: dbData.check_c5 || "", c6: dbData.check_c6 || "",
                        c7: dbData.check_c7 || "", c8: dbData.check_c8 || "", c9: dbData.check_c9 || "",
                        c10: dbData.check_c10 || "", c11: dbData.check_c11 || "", c12: dbData.check_c12 || "",
                        firmaJefeTurno: dbData.firma_jefe_turno ? JSON.parse(dbData.firma_jefe_turno) : null,
                        firmaJefeTrabajo: dbData.firma_jefe_trabajo ? JSON.parse(dbData.firma_jefe_trabajo) : null,
                        firmaRecursoPreventivo: dbData.firma_recurso_preventivo ? JSON.parse(dbData.firma_recurso_preventivo) : null
                    });
                } catch (error) {
                    Swal.fire('Error', 'No se pudo cargar el documento', 'error');
                }
            };
            cargarDatos();
        }
    }, [id, isStandalone]);

    const handleGuardarDirecto = async () => {
        try {
            const token = localStorage.getItem('userToken');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const payload = { ...formData, id_hpt: null };

            if (id) {
                await axios.put(`${API_URL}/api/hpt/carretilla/${id}`, payload, config);
                Swal.fire({
                    title: "¡Actualizado!", text: `Registro actualizado correctamente`, icon: "success",
                    confirmButtonColor: '#003366', timer: 2500
                });
            }else{
                const res = await axios.post(`${API_URL}/api/hpt/carretilla`, payload, config);

                Swal.fire({
                    title: "¡Guardado!", text: `Registrado con ID: ${res.data.id_registro}`, icon: "success",
                    confirmButtonColor: '#003366', timer: 2500
                });
            }
        } catch (error) {
            Swal.fire('Error', 'No se pudo guardar en el servidor.', 'error');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleGuardarFirma = (base64) => {
        const fechaHora = new Date().toLocaleString('es-ES');
        let nombreCompleto = usuarioActual;
        if (!nombreCompleto) {
            const token = localStorage.getItem('userToken');
            if (token) {
                const dec = JSON.parse(atob(token.split('.')[1]));
                nombreCompleto = `${dec.nombre || ''} ${dec.apellidos || ''}`.trim() || dec.user;
            } else {
                nombreCompleto = 'Usuario Desconocido';
            }
        }

        setFormData({
            ...formData,
            [firmaActiva]: { base64: base64, nombre: nombreCompleto, fechaHora: fechaHora }
        });
        setFirmaActiva(null);
    };

    const tieneRespuestasNegativas = itemsChecklist.some(item => formData[item.key] === 'NO');

    const renderCajaFirma = (label, fieldKey) => {
        const firma = formData[fieldKey];
        return (
            <div className="spe-sign-line">
                <span style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '5px', color: '#003366' }}>{label}:</span>
                {firma ? (
                    <div style={{ backgroundColor: '#fff', padding: '5px', borderRadius: '4px', border: '1px solid #ddd', width: '100%', boxSizing: 'border-box' }}>
                        <img src={firma.base64} alt="Firma" style={{ height: '35px', display: 'block', marginBottom: '5px' }} />
                        <div style={{ fontSize: '9px', color: '#555', lineHeight: '1.2' }}>
                            <strong>Por:</strong> {firma.nombre} <br/>
                            <strong>Fecha:</strong> {firma.fechaHora}
                        </div>
                    </div>
                ) : (
                    !readOnly && (
                        <button type="button" className="btn-save" style={{ width: 'fit-content', padding: '4px 10px', fontSize: '11px', backgroundColor: '#003366', marginTop: 'auto' }} onClick={() => setFirmaActiva(fieldKey)}>
                            ✍️ Firmar
                        </button>
                    )
                )}
            </div>
        );
    };

    const handleDescargarPDF = () => {
        setIsPrintingLocal(true);
        Swal.fire({ title: 'Generando documento...', text: 'Por favor espera', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        setTimeout(() => {
            const element = pdfRef.current;
            const opt = {
                margin:       [10, 5, 10, 5],
                filename:     `Permiso_${new Date().getTime()}.pdf`,
                image:        { type: 'jpeg', quality: 0.98 },
                html2canvas:  { scale: 2, useCORS: true, windowWidth: 720 },
                jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
                pagebreak:    { mode: ['css', 'legacy'] }
            };
            html2pdf().set(opt).from(element).save().then(() => {
                setIsPrintingLocal(false);
                Swal.close();
            });
        }, 800);
    };

    const contenidoFormulario = (
        <div className={`pe-container ${isPdfMode || isPrintingLocal ? 'pe-pdf-mode' : 'pe-modal-content'}`}>
            <div ref={pdfRef}
                className={`pe-container ${isPdfMode || isPrintingLocal ? 'pe-pdf-mode' : 'pe-modal-content'}`}
                style={isPrintingLocal ? { width: '720px', maxWidth: '720px', margin: '0 auto', backgroundColor: '#fff', transform: 'none' } : {}}

            >
                
                <header className="spe-header">
                    <div className="spe-logo"><img src={imagenFondo} alt="Logo" style={{ maxHeight: '40px' }} /></div>
                    <div className="spe-title-box">
                        <h2 className="spe-main-title">COMPROBACIÓN DE SEGURIDAD CARRETILLA ELEVADORA</h2>
                    </div>
                    <div className="spe-code">
                        <span className="info-label">Identificación del Equipo:</span>
                        <input type="text" name="numeroSerie" value={formData.numeroSerie} onChange={handleChange} disabled={readOnly} className="spe-input-id" placeholder="CARR-01" />
                    </div>
                </header>

                <section className="spe-section">
                    <div className="spe-dat_sol-2">
                        <div className="spe-input-group">
                            <span className="info-label">FECHA:</span>
                            <input type="date" name="fechaInspeccion" value={formData.fechaInspeccion} onChange={handleChange} disabled={readOnly} className="spe-input" />
                        </div>
                        <div className="spe-input-group">
                            <span className="info-label">HORA:</span>
                            <input type="text" name="horaInspeccion" value={formData.horaInspeccion} onChange={handleChange} disabled={readOnly} className="spe-input" />
                        </div>
                    </div>
                </section>
                
                <section className="spe-section">
                    <h3 className="spe-section-title">Comprobaciones previas al inicio de los trabajos</h3>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', backgroundColor: '#eee', padding: '1px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 15mm 15mm 15mm', backgroundColor: '#003366', color: '#fff', padding: '6px 10px', fontSize: '11px', fontWeight: 'bold', textAlign: 'center', alignItems: 'center' }}>
                            <div style={{ textAlign: 'left' }}>Puntos de Comprobación Requeridos</div>
                            <div>SI</div>
                            <div>NO</div>
                            <div>N/A</div>
                        </div>

                        {itemsChecklist.map((item, index) => (
                            <div key={item.key} style={{ display: 'grid', gridTemplateColumns: '1fr 15mm 15mm 15mm', backgroundColor: index % 2 === 0 ? '#fff' : '#f5f7fa', padding: '6px 10px', fontSize: '11px', alignItems: 'center', minHeight: '32px' }}>
                                <div style={{ paddingRight: '10px', color: '#333' }}>{item.label}</div>
                                <div style={{ textAlign: 'center' }}>
                                    <input type="radio" name={item.key} value="SI" checked={formData[item.key] === 'SI'} onChange={handleChange} disabled={readOnly} />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <input type="radio" name={item.key} value="NO" checked={formData[item.key] === 'NO'} onChange={handleChange} disabled={readOnly} />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <input type="radio" name={item.key} value="NA" checked={formData[item.key] === 'NA'} onChange={handleChange} disabled={readOnly} />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="spe-section">
                    <h3 className="spe-section-title">Observaciones</h3>
                    <div style={{ padding: '8px' }}>
                        <textarea name="observaciones" rows="2" value={formData.observaciones || ''} onChange={handleChange} disabled={readOnly} className="main-textarea" style={{ width: '100%', boxSizing: 'border-box', fontSize: '12px' }} placeholder="Escribe aquí observaciones o deficiencias..." />
                    </div>
                </section>

                <div className={`spe-alert ${tieneRespuestasNegativas ? 'spe-alert-danger' : ''}`} style={{ transition: 'all 0.3s ease' }}>
                    ¡¡¡¡¡¡¡¡ATENCIÓN, si has contestado que NO a algunas de estas preguntas, NO PUEDES REALIZAR EL TRABAJO hasta solucionar éste punto!!!!!!!!!!!
                </div>

                <section className="spe-section">
                    <div className="spe-signature-box" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        {renderCajaFirma('Jefe de Turno, firma e iniciales', 'firmaJefeTurno')}
                        {renderCajaFirma('Jefe de Trabajo, firma e iniciales', 'firmaJefeTrabajo')}
                        {renderCajaFirma('Recurso Preventivo, firma e iniciales', 'firmaRecursoPreventivo')}
                    </div>
                </section>
            </div>

            {!isPdfMode && (
                <div className="spe-modal-actions">
                    <button type="button" className="btn-close" onClick={() => isStandalone ? navigate('/dashboard') : onCancel()}>
                        {readOnly ? '❌ Cerrar' : '❌ Cancelar'}
                    </button>
                    <button type="button" className="btn-print" onClick={handleDescargarPDF} style={{ backgroundColor: '#0288d1', color: '#fff', padding: '6px 15px', fontWeight: 'bold' }}>
                        🖨️ Imprimir Hoja
                    </button>
                    {!readOnly && (
                        <button type="button" className="btn-save" style={{ backgroundColor: '#2e7d32' }} onClick={() => isStandalone ? handleGuardarDirecto() : onSave(formData)}>
                            💾 Guardar Anexo
                        </button>
                    )}
                </div>
            )}

            {firmaActiva && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 100000 }}>
                    <SignaturePad onSave={handleGuardarFirma} onCancel={() => setFirmaActiva(null)} />
                </div>
            )}
        </div>
    );

    if (isPdfMode) return contenidoFormulario;

    return (
        <div className={isStandalone ? "container" : "spe-modal-overlay"}>
            {contenidoFormulario}
        </div>
    );
};

export default PermisoCarretilla;